import { useCallback } from "react";
import { ModelName, QueryMethodForModel, SessionMethodReturn, SupportedSession } from "../../types";
import { executeTellescopeMutationSideEffect } from "../../mappings";
import { useQueryClient } from "@tanstack/react-query";
import { TellescopeMutationInternalContext, TellescopeMutationSideEffectStrategy } from "../types";
import { parseISOStrings } from "@tellescope/type-correction";
import { formResponsePredicate } from "../../utils";
import { useTellescopeSession } from "../../context";

export interface UseApplyCacheUpdatesFromDataSyncArgs {
  updates: SessionMethodReturn<"sync">["results"];
  strategy?: TellescopeMutationSideEffectStrategy;
}

export interface UseApplyCacheUpdatesFromDataSyncResults {
  applyUpdates: ({ updates }: UseApplyCacheUpdatesFromDataSyncArgs) => Promise<{
    results: TellescopeMutationInternalContext<ModelName, QueryMethodForModel<ModelName>>[];
  }>;
}

export const useApplyCacheUpdatesFromDataSync = (
  providedSession?: SupportedSession
): UseApplyCacheUpdatesFromDataSyncResults => {
  const queryClient = useQueryClient();
  const session = useTellescopeSession(providedSession);

  const applyUpdates = useCallback<UseApplyCacheUpdatesFromDataSyncResults["applyUpdates"]>(
    async ({ updates, strategy = "update" }) => {
      // Sort updates by timestamp (oldest first) when the data contains a timestamp field.
      // This ensures cache updates are applied in chronological order.
      // Non-timestamped and deleted updates remain in their original positions.
      const timestampedItems: Array<{ originalIndex: number; timestamp: number }> = [];
      updates.forEach((update, i) => {
        if (update.data === "deleted") return;
        try {
          const parsed = JSON.parse(update.data);
          if (parsed.timestamp) {
            timestampedItems.push({ originalIndex: i, timestamp: new Date(parsed.timestamp).getTime() });
          }
        } catch {
          // If parsing fails, skip
        }
      });

      const sortedUpdates = [...updates];
      if (timestampedItems.length > 1) {
        // Positions where timestamped items originally were (sorted ascending)
        const positions = timestampedItems.map((item) => item.originalIndex).sort((a, b) => a - b);

        // Sort timestamped items by timestamp (oldest first)
        const sortedByTimestamp = [...timestampedItems].sort((a, b) => a.timestamp - b.timestamp);

        // Place sorted timestamped items back into their original slot positions
        positions.forEach((pos, i) => {
          const sourceIndex = sortedByTimestamp[i]?.originalIndex;
          const sourceUpdate = sourceIndex != null ? updates[sourceIndex] : undefined;
          if (sourceUpdate) {
            sortedUpdates[pos] = sourceUpdate;
          }
        });
      }

      const results = sortedUpdates.reduce<
        TellescopeMutationInternalContext<ModelName, QueryMethodForModel<ModelName>>[]
      >((acc, update) => {
        if (update.modelName) {
          if (update.data === "deleted") {
            // Apply mutation side effects for the message model
            const result = executeTellescopeMutationSideEffect({
              queryClient,
              model: update.modelName,
              method: "deleteOne",
              request: update.recordId,
              sideEffect: "onSuccess",
              strategy,
              context: {
                client: queryClient,
                meta: undefined,
              },
              onMutateResult: {
                originalQueries: [],
              },
              session: session ?? undefined,
            });
            if (result) {
              acc.push(result);
            }
            return acc;
          }

          let parsedModel;
          try {
            parsedModel = parseISOStrings(JSON.parse(update.data), {
              deep: true,
              deepArrays: true,
              predicate: update.modelName === "form_responses" ? formResponsePredicate : undefined,
            });
            if (!parsedModel.createdAt && "createdAt" in update && update.createdAt) {
              parsedModel.createdAt =
                update.createdAt instanceof Date
                  ? update.createdAt.toISOString()
                  : typeof update.createdAt === "string"
                  ? update.createdAt
                  : new Date().toISOString();
            }
          } catch (error) {
            // Skip malformed data
            console.error("Failed to parse data-sync update data:", error);
            return acc;
          }

          // Apply mutation side effects for the message model
          const result = executeTellescopeMutationSideEffect({
            queryClient,
            model: update.modelName,
            method: "updateOne",
            request: parsedModel,
            response: parsedModel,
            sideEffect: "onSuccess",
            strategy,
            context: {
              client: queryClient,
              meta: undefined,
            },
            onMutateResult: {
              originalQueries: [],
            },
            session: session ?? undefined,
          });

          if (result) {
            acc.push(result);
          }
        }
        return acc;
      }, []);

      return { results };
    },
    [queryClient, session]
  );

  return {
    applyUpdates,
  };
};
